import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UUID } from "crypto";
import api from "@/app/api";
import { partnerMenuKeys } from "../keys/partnerMenu";
import type {
  PartnerMenuItemCreate,
  PartnerMenuItemUpdate,
  PartnerMenuItemProductCreate,
  PartnerMenuItemProductUpdate,
  StoppedProductOverrideRequest,
} from "@/app/api/types/partnerMenu";

// Menu Item queries
export function usePartnerMenuItems(partnerId: UUID | null) {
  return useQuery({
    queryKey: partnerMenuKeys.partnerMenuItems(partnerId!),
    queryFn: () => api.partnerMenu.getPartnerMenuItems(partnerId!),
    enabled: !!partnerId,
  });
}

export function useMenuItem(menuItemId: UUID | null) {
  return useQuery({
    queryKey: partnerMenuKeys.menuItem(menuItemId!),
    queryFn: () => api.partnerMenu.getMenuItem(menuItemId!),
    enabled: !!menuItemId,
  });
}

export function useMenuItemProducts(menuItemId: UUID | null) {
  return useQuery({
    queryKey: partnerMenuKeys.menuItemProducts(menuItemId!),
    queryFn: () => api.partnerMenu.getMenuItemProducts(menuItemId!),
    enabled: !!menuItemId,
  });
}

// Menu Item mutations
export function useCreateMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      partnerId,
      data,
    }: {
      partnerId: UUID;
      data: PartnerMenuItemCreate;
    }) => api.partnerMenu.createMenuItem(partnerId, data),
    onSuccess: (_, { partnerId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.partnerMenuItems(partnerId),
      });
    },
  });
}

export function useUpdateMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      menuItemId,
      partnerId,
      data,
    }: {
      menuItemId: UUID;
      partnerId: UUID;
      data: PartnerMenuItemUpdate;
    }) => api.partnerMenu.updateMenuItem(menuItemId, data),
    onSuccess: (_, { partnerId, menuItemId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.partnerMenuItems(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItem(menuItemId),
      });
    },
  });
}

export function useDeleteMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      menuItemId,
      partnerId,
    }: {
      menuItemId: UUID;
      partnerId: UUID;
    }) => api.partnerMenu.deleteMenuItem(menuItemId),
    onSuccess: (_, { partnerId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.partnerMenuItems(partnerId),
      });
    },
  });
}

// Product Mapping mutations
export function useAddProductToMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      menuItemId,
      partnerId,
      data,
    }: {
      menuItemId: UUID;
      partnerId: UUID;
      data: PartnerMenuItemProductCreate;
    }) => api.partnerMenu.addProductToMenuItem(menuItemId, data),
    onSuccess: (_, { partnerId, menuItemId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.partnerMenuItems(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItem(menuItemId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItemProducts(menuItemId),
      });
    },
  });
}

export function useUpdateProductMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mappingId,
      menuItemId,
      partnerId,
      data,
    }: {
      mappingId: UUID;
      menuItemId: UUID;
      partnerId: UUID;
      data: PartnerMenuItemProductUpdate;
    }) => api.partnerMenu.updateProductMapping(mappingId, data),
    onSuccess: (_, { partnerId, menuItemId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.partnerMenuItems(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItem(menuItemId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItemProducts(menuItemId),
      });
    },
  });
}

export function useRemoveProductFromMenuItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mappingId,
      menuItemId,
      partnerId,
    }: {
      mappingId: UUID;
      menuItemId: UUID;
      partnerId: UUID;
    }) => api.partnerMenu.removeProductFromMenuItem(mappingId),
    onSuccess: (_, { partnerId, menuItemId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.partnerMenuItems(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItem(menuItemId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItemProducts(menuItemId),
      });
    },
  });
}

export function useOverrideStoppedStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      mappingId,
      menuItemId,
      partnerId,
      data,
    }: {
      mappingId: UUID;
      menuItemId: UUID;
      partnerId: UUID;
      data: StoppedProductOverrideRequest;
    }) => api.partnerMenu.overrideStoppedStatus(mappingId, data),
    onSuccess: (_, { partnerId, menuItemId }) => {
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.partnerMenuItems(partnerId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItem(menuItemId),
      });
      queryClient.invalidateQueries({
        queryKey: partnerMenuKeys.menuItemProducts(menuItemId),
      });
    },
  });
}
